import { useState } from 'react';

import { Alert, Button } from '@shared';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

import { FilePicker } from '@/components/FilePicker';
import { useAuth } from '@/features/auth/useAuth';
import { useLocale, useTranslations } from '@/i18n/client';
import { errorKeyOf, type ApiErrorKey } from '@/services/api/errors';
import { OrdersApi } from '@/services/api/orders-api';
import { useUploadProgress } from '@/features/upload/useUploadProgress';
import type { UploadProgressOperation } from '@/features/upload/upload-progress-context';

const MAX_FILE_SIZE_BYTES = 300 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = new Set(['.stl', '.ply', '.obj']);
const ACCEPT_ATTRIBUTE = '.stl,.STL,.ply,.PLY,.obj,.OBJ';
const SCAN_FIELDS = ['upper', 'lower', 'vestibular', 'allInOne'] as const;

type ScanField = (typeof SCAN_FIELDS)[number];
type SelectedScanFiles = Partial<Record<ScanField, File>>;

interface ScanRevisionUploadProps {
  orderId: string;
  onCompleted: (message: string) => void;
}

function isSupportedFile(file: File): boolean {
  const extension = file.name.slice(file.name.lastIndexOf('.')).toLocaleLowerCase('en-US');
  return ACCEPTED_EXTENSIONS.has(extension);
}

export function ScanRevisionUpload({ orderId, onCompleted }: ScanRevisionUploadProps) {
  const locale = useLocale();
  const t = useTranslations('common.scanRevision');
  const tOrderDetails = useTranslations('common.orderDetails');
  const tErrors = useTranslations('errors.api');
  const { client } = useAuth();
  const { beginUpload } = useUploadProgress();
  const [selectedFiles, setSelectedFiles] = useState<SelectedScanFiles>({});
  const [message, setMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<ApiErrorKey | null>(null);
  const [cleanupErrorKey, setCleanupErrorKey] = useState<ApiErrorKey | null>(null);
  const [failedFileName, setFailedFileName] = useState<string | null>(null);
  const [processingFileName, setProcessingFileName] = useState<string | null>(null);

  const selectedEntries = SCAN_FIELDS.flatMap((field) => {
    const file = selectedFiles[field];
    return file ? [{ field, file }] : [];
  });

  function handleFileChange(field: ScanField, event: React.ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';

    if (!file) {
      return;
    }

    setSuccessMessage(null);
    if (!isSupportedFile(file)) {
      setValidationMessage(t('upload.invalidExtension', { file: file.name }));
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setValidationMessage(t('upload.tooLarge', { file: file.name }));
      return;
    }

    setSelectedFiles((current) => ({ ...current, [field]: file }));
    setValidationMessage(null);
    setErrorKey(null);
    setFailedFileName(null);
  }

  function removeFile(field: ScanField): void {
    setSelectedFiles((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });
    setValidationMessage(null);
    setSuccessMessage(null);
    setErrorKey(null);
  }

  async function handleUpload(): Promise<void> {
    if (!client || !orderId || selectedEntries.length === 0 || isUploading) {
      if (selectedEntries.length === 0) {
        setValidationMessage(t('upload.empty'));
      }
      return;
    }

    setIsUploading(true);
    setValidationMessage(null);
    setSuccessMessage(null);
    setErrorKey(null);
    setCleanupErrorKey(null);
    setFailedFileName(null);
    setProcessingFileName(null);

    let uploadProgress: UploadProgressOperation | null = null;
    let operationFileName: string | null = null;
    let api: OrdersApi | null = null;
    let preparedFileIds: string[] = [];
    let uploadCompleted = false;
    let completionAttempted = false;

    try {
      uploadProgress = beginUpload(selectedEntries.length);
      if (!uploadProgress) {
        return;
      }

      api = new OrdersApi(client);
      const preparedFiles = await api.prepareScanRevisionFiles({
        orderId,
        files: selectedEntries.map(({ file }) => file),
      });
      const requiredFiles = preparedFiles.filter((file) => file.uploadRequired !== false);
      preparedFileIds = requiredFiles.map((file) => file.id).filter(Boolean);
      if (preparedFiles.length !== selectedEntries.length) {
        setErrorKey('generic');
        setFailedFileName(selectedEntries[preparedFiles.length]?.file.name ?? null);
        return;
      }

      uploadProgress.setTotalFiles(requiredFiles.length);
      if (requiredFiles.length === 0) {
        uploadProgress.finish();
        setSelectedFiles({});
        setSuccessMessage(tOrderDetails('additionalFilesSection.alreadyUploaded'));
        return;
      }

      for (let index = 0; index < selectedEntries.length; index += 1) {
        const entry = selectedEntries[index];
        const preparedFile = preparedFiles[index];
        if (!entry || !preparedFile?.id) {
          setErrorKey('generic');
          setFailedFileName(entry?.file.name ?? null);
          return;
        }

        if (preparedFile.uploadRequired === false) {
          continue;
        }

        operationFileName = entry.file.name;
        setProcessingFileName(operationFileName);
        uploadProgress.startFile(operationFileName);
        await api.uploadAdditionalOrderFile(preparedFile.id, entry.file, {
          sendNotification: true,
          message,
          language: locale,
          onProgress: (percentage) => uploadProgress?.setFileProgress(percentage),
        });
        uploadProgress.completeFile();
        operationFileName = null;
        setProcessingFileName(null);
      }

      completionAttempted = true;
      await api.completeScanRevision({
        orderId,
        fileIds: requiredFiles.map((file) => file.id),
      });
      uploadCompleted = true;
      setSelectedFiles({});
      setMessage('');
      onCompleted(
        requiredFiles.length < selectedEntries.length
          ? tOrderDetails('additionalFilesSection.partialSuccess')
          : t('success.uploaded'),
      );
    } catch (error) {
      setFailedFileName(operationFileName);
      setErrorKey(errorKeyOf(error));
    } finally {
      if (!uploadCompleted && api) {
        let cleanupError: unknown;
        let shouldCleanup = true;

        if (completionAttempted) {
          try {
            const order = await api.getProjectOrder(orderId);
            const orderState = order.orderState
              .trim()
              .toLocaleLowerCase('de')
              .replace(/[_\s-]+/g, '');
            if (orderState === 'toconfirm') {
              uploadCompleted = true;
              setErrorKey(null);
              setFailedFileName(null);
              setSelectedFiles({});
              setMessage('');
              onCompleted(t('success.uploaded'));
              shouldCleanup = false;
            } else if (orderState !== 'scanrevision' && orderState !== 'scanrevisionnewupload') {
              cleanupError = new Error('The order state could not confirm the scan upload result.');
              shouldCleanup = false;
            }
          } catch (error) {
            cleanupError = error;
            shouldCleanup = false;
          }
        }

        if (shouldCleanup) {
          for (const fileId of preparedFileIds) {
            try {
              await api.deleteAdditionalOrderFile({ orderId, fileId });
            } catch (error) {
              cleanupError ??= error;
            }
          }
        }
        setCleanupErrorKey(cleanupError ? errorKeyOf(cleanupError) : null);
      }
      uploadProgress?.finish();
      setProcessingFileName(null);
      setIsUploading(false);
    }
  }

  return (
    <section className="rounded-[var(--zs-radius-m)] border border-[var(--md-border)] bg-[var(--md-white)] p-6 shadow-[var(--md-shadow-soft)] lg:col-span-2 max-sm:p-4">
      <div>
        <span className="md-review__section-eyebrow">{t('upload.eyebrow')}</span>
        <h2 className="mb-[0.35rem] text-[1.2rem]">{t('upload.title')}</h2>
        <p className="m-0 text-[var(--zs-muted)]">{t('upload.description')}</p>
      </div>

      <div className="md-case-grid md-case-grid--two mt-4">
        {SCAN_FIELDS.map((field) => {
          const file = selectedFiles[field];
          return (
            <FilePicker
              key={field}
              label={t(`upload.${field}`)}
              buttonText={t('upload.chooseFile')}
              accept={ACCEPT_ATTRIBUTE}
              selectedFiles={file ? [file] : []}
              trailingAction={
                file ? (
                  <Button
                    type="button"
                    variant="danger"
                    className="shrink-0 self-center"
                    disabled={isUploading}
                    onClick={() => removeFile(field)}
                    aria-label={`${t('upload.remove')}: ${file.name}`}
                    title={t('upload.remove')}
                  >
                    {t('upload.remove')}
                  </Button>
                ) : null
              }
              disabled={isUploading}
              onChange={(event) => handleFileChange(field, event)}
            />
          );
        })}
      </div>
      <p className="mt-3 text-sm text-[var(--zs-muted)]">{t('upload.hint')}</p>

      {processingFileName ? (
        <p
          className="mt-3 flex items-center gap-2 text-sm text-[var(--zs-muted)]"
          role="status"
          aria-live="polite"
        >
          <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />
          {t('upload.uploadingFile', { file: processingFileName })}
        </p>
      ) : null}

      <label
        className="mt-4 block text-sm font-bold text-[var(--md-primary)]"
        htmlFor="scan-revision-message"
      >
        {t('upload.messageLabel')}
      </label>
      <textarea
        id="scan-revision-message"
        className="mt-1.5 min-h-24 w-full resize-y rounded-[var(--zs-radius-s)] border border-[var(--md-border)] bg-[var(--md-white)] px-3 py-2.5 text-sm text-[var(--zs-text)] outline-none focus:border-[var(--md-highlight)]"
        maxLength={4000}
        placeholder={t('upload.messagePlaceholder')}
        value={message}
        disabled={isUploading}
        onChange={(event) => setMessage(event.target.value)}
      />

      {successMessage ? (
        <div className="mt-3">
          <Alert variant="info">{successMessage}</Alert>
        </div>
      ) : null}
      {validationMessage ? (
        <p className="mt-3 text-sm text-[var(--md-highlight)]">{validationMessage}</p>
      ) : null}
      {errorKey ? (
        <p className="mt-3 text-sm text-[var(--md-highlight)]" role="alert">
          {failedFileName ? `${t('upload.fileFailed', { file: failedFileName })} ` : null}
          {tErrors(errorKey)}
        </p>
      ) : null}
      {cleanupErrorKey ? (
        <p className="mt-3 text-sm text-[var(--md-highlight)]" role="alert">
          {t('upload.cleanupFailed')} {tErrors(cleanupErrorKey)}
        </p>
      ) : null}
      <Button
        className="mt-4"
        disabled={selectedEntries.length === 0}
        loading={isUploading}
        loadingLabel={t('upload.uploading')}
        onClick={() => void handleUpload()}
      >
        {t('upload.uploadAction')}
      </Button>
      {selectedEntries.length === 0 ? (
        <ul className="mt-2 list-disc pl-5 text-sm text-[var(--zs-muted)]" role="status" aria-live="polite">
          <li>{t('upload.empty')}</li>
        </ul>
      ) : null}
    </section>
  );
}
